import React from 'react';

export type BannerProps = {
  title: React.ReactNode;
  subtitle: string;
  subheading?: string;
  icon?: React.ReactNode;
  paddingVariant?: 'default' | 'large';
  imageVariant?: 'withImage' | 'noImage' | 'withVideo';
  colorVariant?: 'gradient' | 'white' | 'black';
  showCTAs?: boolean;
  breadcrumbs?: React.ReactNode;
  graphicOverlay?: React.ReactNode;
  showDefaultGraphic?: boolean;
  videoCaption?: string;
  header?: React.ReactNode;
};

function highlightCompanion(text: React.ReactNode) {
  if (typeof text !== 'string') {
    return text;
  }
  // Replace only the first occurrence of 'Companion' (case-sensitive)
  const parts = text.split(/(Companion)/);
  return parts.map((part, i) =>
    part === 'Companion' ? (
      <span key={i} style={{ color: '#1ae4ff' }}>{part}</span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

const Banner: React.FC<BannerProps> = ({
  title,
  subtitle,
  subheading,
  icon,
  paddingVariant = 'default',
  imageVariant = 'withImage',
  colorVariant = 'gradient',
  showCTAs = false,
  breadcrumbs,
  graphicOverlay,
  showDefaultGraphic = false,
  videoCaption,
  header,
}) => {
  const getBackgroundStyle = () => {
    switch (colorVariant) {
      case 'gradient':
        return {
          background: 'radial-gradient(circle farthest-corner at 30% 50%, #00363B, #111827 80%)',
        };
      default:
        return {};
    }
  };

  const paddingClasses = paddingVariant === 'large' ? 'py-10 md:py-16' : 'py-6 md:py-10';

  return (
<div
  id="banner"
  className="relative overflow-hidden dark:bg-gray-950"
  style={getBackgroundStyle()}
>


      {/* Overlay ie-banner-graphic.png at the bottom */}
      {showDefaultGraphic && (
        <img
          src="/logos/ie-graphic.svg"
          alt="ie banner graphic"
          className="pointer-events-none select-none absolute left-0 right-0 bottom-16 w-full max-h-46 object-cover opacity-10 z-0"
          style={{ objectPosition: 'top' }}
        />
      )}
      {graphicOverlay}
      {/* Subtle background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-72 h-72 bg-teal-500/5 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-2xl translate-x-1/2 translate-y-1/2"></div>
      </div>



      <section className={`relative max-w-7xl mx-auto px-4 ${paddingClasses} min-h-[280px] flex items-center pt-16`}>
        {breadcrumbs && (
          <div className="absolute left-0 top-2 w-full z-10">
            {breadcrumbs}
          </div>
        )}
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-stretch h-full${breadcrumbs ? ' mt-12' : ''}`}>
          {/* Content Section */}
          <div className="space-y-6 w-full flex flex-col justify-center col-span-1 lg:col-span-7">
            {/* Title & Subtitle Block with consistent width */}
            <div className="space-y-4 max-w-4xl">
              {subheading && (
                <p className="text-sm font-semibold text-teal-400 uppercase tracking-wide mb-2">
                  {subheading}
                </p>
              )}
              {/* If title is a React element with a badge, render badge above heading */}
              {React.isValidElement(title) && Array.isArray((title.props as any)?.children) && (title.props as any).children.length === 2 ? (
                <>
                  <span className="inline-block mb-2">{(title.props as any).children[1]}</span>
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white dark:text-gray-100 leading-tight">
                    {highlightCompanion((title.props as any).children[0])}
                  </h1>
                </>
              ) : (
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white dark:text-gray-100 leading-tight">
                  {highlightCompanion(title)}
                </h1>
              )}
              {/* Subtitle */}
              <p className="text-lg md:text-xl text-white dark:text-gray-300 leading-relaxed max-w-2xl">
                {subtitle}
              </p>
            </div>
          </div>

          {/* Icon/Image/Video Section */}
          {imageVariant === 'withImage' && icon && (
            <div className="relative w-full h-full flex-none flex items-center justify-end col-span-1 lg:col-span-5 p-0 m-0">
              <div className="w-full h-full p-0 m-0 flex items-center justify-center">{icon}</div>
            </div>
          )}
          
          {/* Video Section */}
          {imageVariant === 'withVideo' && icon && (
            <div className="relative w-full aspect-video rounded-lg overflow-hidden shadow-lg">
              <div className="absolute inset-0">
                {React.isValidElement(icon)
                  ? React.cloneElement(icon as React.ReactElement<React.VideoHTMLAttributes<HTMLVideoElement>>, {
                      className: [
                        (icon.props ? (icon.props as any).className : ''),
                        'w-full h-full object-cover absolute top-0 left-0'
                      ].join(' ')
                    })
                  : icon}
              </div>
              {videoCaption && (
                <p className="text-sm text-white/80 mt-2 text-center lg:text-left absolute bottom-0 left-0 w-full bg-black/50 p-2">
                  {videoCaption}
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      <style>
        {`
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
    </div>
  );
};

export default Banner; 